import React, { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, FileSignature, Lock, LogOut, PenLine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { api, errMsg, fmtDay } from "../../lib/api";
import { invalidatePolicyAccess } from "../../lib/policies";
import { useAuth } from "../../context/AuthContext";
import { PolicyPdfViewer } from "./PolicyPdfViewer";

/**
 * Phase 218 — full-screen signing flow. Shows the pending policies ONE BY ONE; each needs the "I have read and accept"
 * tick and the staff member's full name typed exactly as on record. Nothing else of the app is reachable until the
 * last one is signed (the caller renders this instead of the portal).
 *
 * Props: code, policies (pending list), onDone(), onSigned(policy) optional, embedded (no full-screen chrome).
 */
export function PolicySignFlow({ code, policies, onDone, onSigned, embedded = false, nameOnRecord }) {
  const { auth, logout } = useAuth();
  const [queue, setQueue] = useState(policies || []);
  const [idx, setIdx] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // read-through check: the acceptance box unlocks only after the last page has been scrolled into view
  const [read, setRead] = useState({ percent: 0, pagesRead: 0, pages: 0, reachedEnd: false, tracked: true });
  useEffect(() => { setQueue(policies || []); setIdx(0); }, [policies]);
  const total = queue.length;
  const policy = queue[idx];
  const expected = nameOnRecord || auth?.user?.name || "";
  const norm = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();
  const nameOk = norm(typed) === norm(expected) && norm(typed).length > 0;
  const readOk = read.reachedEnd;

  useEffect(() => { setAccepted(false); setTyped(""); setErr(null); setRead({ percent: 0, pagesRead: 0, pages: 0, reachedEnd: false, tracked: true }); }, [policy?.id]);

  if (!policy) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (!readOk) { setErr("Please scroll through the whole document to its last page first."); return; }
    if (!accepted) { setErr("Please tick \"I have read and accept this policy\" first."); return; }
    if (!nameOk) { setErr(`Type your full name exactly as on record: "${expected}"`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/policies/${policy.id}/sign`, {
        typed_name: typed.trim(), accepted: true,
        read_to_end: !!(read.reachedEnd && read.tracked), pages_read: read.tracked ? (read.pages || null) : null,
      });
      toast.success(`Signed: ${policy.title}`);
      onSigned?.(policy, r.data);
      invalidatePolicyAccess();
      // the server tells us what is still pending — follow it so a policy added meanwhile is not skipped
      const remaining = r.data?.remaining ?? (total - idx - 1);
      if (remaining <= 0) { onDone?.(); return; }
      if (r.data?.next && !queue.some((p) => p.id === r.data.next.id)) setQueue((q) => [...q, r.data.next]);
      setIdx((i) => i + 1);
    } catch (ex) {
      const msg = errMsg(ex);
      setErr(msg);
      if (ex?.response?.status === 409 || ex?.response?.status === 403) {
        // already signed / no longer selected: move on
        toast.info(msg);
        if (idx + 1 >= total) onDone?.(); else setIdx((i) => i + 1);
      }
    } finally { setBusy(false); }
  };

  const body = (
    <Card className="w-full max-w-4xl shadow-md" data-testid="policy-sign-card">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <FileSignature className="h-4 w-4" />
              <span data-testid="policy-sign-progress">Policy {idx + 1} of {total}</span>
              <Badge variant="outline" className="text-[10px]" data-testid="policy-sign-version">v{policy.version}</Badge>
              {policy.revision_reason === "new_version" && <Badge variant="secondary" className="text-[10px]">Updated version</Badge>}
              {policy.revision_reason === "edited" && <Badge variant="secondary" className="text-[10px]">Updated details</Badge>}
            </div>
            <h2 className="text-lg sm:text-xl font-semibold font-heading tracking-tight" data-testid="policy-sign-title">{policy.title}</h2>
            {policy.description && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line" data-testid="policy-sign-description">{policy.description}</p>}
            <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5" data-testid="policy-sign-effective">
              <CalendarDays className="h-3.5 w-3.5" /> Effective from {fmtDay(policy.effective_date)}
            </p>
          </div>
          {!embedded && (
            <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => { logout(); }} data-testid="policy-sign-logout">
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          )}
        </div>

        <PolicyPdfViewer code={code} policy={policy} onReadProgress={setRead} />

        <form onSubmit={submit} className="space-y-3" data-testid="policy-sign-form">
          {readOk ? (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200" data-testid="policy-read-complete">
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{read.tracked ? `You have reached the end of the document${read.pages ? ` (${read.pages} page${read.pages === 1 ? "" : "s"})` : ""}. You can now accept and sign.` : "This browser shows the document in its own viewer — please read it fully, then accept and sign."}</span>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200 space-y-1.5" data-testid="policy-read-progress">
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Read the whole document — scroll to the last page to unlock the acceptance box.{read.pages ? ` Page ${Math.max(1, read.pagesRead)} of ${read.pages}.` : ""}</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-amber-200/70 dark:bg-amber-900/60 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={read.percent} aria-label="Reading progress">
                <div className="h-full rounded-full bg-amber-600 dark:bg-amber-400 transition-[width] duration-200" style={{ width: `${read.percent}%` }} data-testid="policy-read-progress-bar" />
              </div>
            </div>
          )}
          <div className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${readOk ? "bg-muted/30" : "bg-muted/50 opacity-70"}`} data-testid="policy-sign-accept-row">
            <Checkbox id="policy-accept" checked={accepted} disabled={!readOk} onCheckedChange={(v) => setAccepted(v === true)} className="mt-0.5" data-testid="policy-sign-accept" aria-describedby="policy-accept-hint" />
            <div className="min-w-0">
              <Label htmlFor="policy-accept" className={`text-sm leading-snug ${readOk ? "cursor-pointer" : "cursor-not-allowed"}`}>I have read and accept this policy</Label>
              {!readOk && <p id="policy-accept-hint" className="text-[11px] text-muted-foreground mt-0.5" data-testid="policy-sign-accept-locked">Unlocks after you reach the last page</p>}
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="policy-typed-name" className="text-sm">Digital signature — type your full name</Label>
            <div className="relative">
              <PenLine className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="policy-typed-name" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={expected} autoComplete="off"
                className={`pl-9 h-11 font-heading text-base ${typed && !nameOk ? "border-destructive focus-visible:ring-destructive" : nameOk ? "border-emerald-500" : ""}`}
                aria-invalid={!!typed && !nameOk} data-testid="policy-sign-name" />
            </div>
            <p className="text-[11px] text-muted-foreground">Must match your name on record: <span className="font-medium text-foreground" data-testid="policy-sign-expected">{expected}</span></p>
          </div>
          {err && <p className="text-sm text-destructive" role="alert" data-testid="policy-sign-error">{err}</p>}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Your name, date and time are recorded as your signature.</p>
            <Button type="submit" size="lg" disabled={busy || !readOk || !accepted || !nameOk} data-testid="policy-sign-submit">
              {busy ? "Signing..." : idx + 1 < total ? "Sign and continue" : "Sign and enter the app"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );

  if (embedded) return body;
  return (
    <div className="min-h-screen bg-background flex flex-col items-center p-3 sm:p-6" data-testid="policy-sign-screen">
      <div className="w-full max-w-4xl mb-3 flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-heading font-semibold text-foreground">{auth?.municipality?.short_name || auth?.municipality?.name || "MunicipalConnect"}</span>
        <span data-testid="policy-sign-user">{auth?.user?.name}{auth?.user?.employee_id ? ` (${auth.user.employee_id})` : ""}</span>
      </div>
      <p className="w-full max-w-4xl text-sm text-muted-foreground mb-3" data-testid="policy-sign-intro">
        {total === 1 ? "A policy needs your signature before you continue." : `${total} policies need your signature before you continue — one at a time.`}
      </p>
      {body}
    </div>
  );
}

// one check per signed-in account per browser session — a reload re-checks, as does a new login
const checked = {};

/**
 * Gate used by the router: fetches the pending list once per session for staff roles and shows the signing flow
 * instead of the portal while anything is pending. Citizens, the Super Admin and marshals without pending items
 * pass straight through.
 */
export function PolicySignGate({ code, children }) {
  const { auth } = useAuth();
  const uid = auth?.user?.id;
  const role = auth?.user?.role;
  const staff = !!uid && !!code && role && role !== "citizen" && role !== "super_admin";
  const key = `${code}:${uid}`;
  const [state, setState] = useState(() => (checked[key] ? { done: true, pending: [] } : { done: !staff, pending: [] }));
  useEffect(() => {
    if (!staff) { setState({ done: true, pending: [] }); return undefined; }
    if (checked[key]) { setState({ done: true, pending: [] }); return undefined; }
    let alive = true;
    api.get(`/t/${code}/policies/pending`)
      .then((r) => {
        if (!alive) return;
        const items = r.data?.items || [];
        if (items.length === 0) checked[key] = true;
        setState({ done: true, pending: items, name: r.data?.name_on_record });
      })
      .catch(() => { if (alive) { checked[key] = true; setState({ done: true, pending: [] }); } });   // never lock people out on a network error
    return () => { alive = false; };
  }, [code, uid, staff, key]);

  if (!state.done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="policy-gate-loading">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-label="Checking company policies" />
      </div>
    );
  }
  if (state.pending.length > 0) {
    return <PolicySignFlow code={code} policies={state.pending} nameOnRecord={state.name}
      onDone={() => { checked[key] = true; setState({ done: true, pending: [] }); }} />;
  }
  return children;
}

export function resetPolicyGate() { Object.keys(checked).forEach((k) => delete checked[k]); }
